import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faTrash, faEdit, faAngleRight, faExpand } from '@fortawesome/free-solid-svg-icons';
import { Button, Modal, Form, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import YouTube from 'react-youtube';

import config from '../config/config';
import { useResto } from '../contexts/RestoContext';
import { useMode } from '../contexts/ModeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ucfirst } from '../utils/utils';

function MediaItem({ media, 
                     handleDelete,  
                     handleEdit, 
                     handleRankingUpdate,
                     isFirst,
                     isLast,
                     isShortLine = true }) {

    const { t } = useTranslation();
    const { isModeAdmin } = useMode();
    const { restoData } = useResto();
    const { activeLanguage } = useLanguage();
    const availableLanguages = restoData.availableLanguages;

    const languageKey = ucfirst(activeLanguage);
    const label = media[`label${languageKey}`] || '';
    const details = media[`details${languageKey}`] || '';

    const getPhotoUrl = (media, format) => {
        return `${config.documentsUrl}photos/${format}/${media.uuid}.jpg`;
    };

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showFullScreen, setShowFullScreen] = useState(false);
    const [editedMedia, setEditedMedia] = useState({
        ...media,
    });

    const handleTrashClick = () => {
        setShowConfirmation(true);
    };

    const handleCloseConfirmation = () => {
        setShowConfirmation(false);
    };

    const handleConfirmDelete = () => {
        handleDelete(media.id);
        setShowConfirmation(false);
    };

    const handleRanking = (direction) => {
        handleRankingUpdate(direction, media.id);
    };

    const handleEditClick = () => {
        setEditedMedia({
            ...media,
        });
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
    };

    const handleEditSubmit = () => {
        handleEdit(editedMedia);
        setShowEditModal(false);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditedMedia((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const toggleDetails = () => {
        setShowDetails(!showDetails);
    };

    const toggleFullScreen = () => {
        setShowFullScreen(!showFullScreen);
    };

    const opts = {
        playerVars: {
          autoplay: 0,
        },
      };

      const extractVideoId = (url) => {
        const match = url.match(/[?&]v=([^&#]*)/);
        return match && match[1];
    };

    return (
        <div className="media-container">
            <div className="text-center" style={{ position: 'relative' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    
                { (media.mediaType === 'photo') && 
                <><img src={getPhotoUrl(media, 'medium')} alt={label} />

                <button 
                    className="btn btn-primary btn-sm"  
                    style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1 }} 
                    onClick={toggleFullScreen}>
                    <FontAwesomeIcon icon={faExpand} />
                </button></>}

                { (media.mediaType === 'video') &&         
                <div className="video-container">
                    <YouTube videoId={extractVideoId(media.videoUrl)} opts={opts} className="react-player" />
                </div>}


                </div>
      {/* Admin UI */}
      {isModeAdmin && (
                    <>
                        <div className="media-label" style={{ marginTop: '10px' }}>
                            <strong>
                                <i>{label}</i>
                            </strong>

                            {details && (
                                <button className="btn btn-primary btn-sm expand-button"  style={{ margin: '5px' }} onClick={toggleDetails}>
                                    {t('DETAILS')} <FontAwesomeIcon icon={showDetails ? faAngleDown : faAngleRight} />
                                </button>
                            )}

                            <Button variant="primary" className="btn-sm"  style={{ margin: '5px' }} onClick={handleEditClick}>
                                <FontAwesomeIcon icon={faEdit} />
                            </Button>

                            {handleRankingUpdate && !isFirst && (
                                <Button variant="primary" className="btn-sm"  style={{ margin: '5px' }} onClick={() => handleRanking('up')}>
                                    <FontAwesomeIcon icon={faAngleUp} />
                                </Button>
                            )}
                            {handleRankingUpdate && !isLast && (
                                <Button variant="primary" className="btn-sm"  style={{ margin: '5px' }} onClick={() => handleRanking('down')}>
                                    <FontAwesomeIcon icon={faAngleDown} />
                                </Button>
                            )}
                            
                            <Button variant="primary" className="btn-sm"  style={{ margin: '5px' }} onClick={handleTrashClick}>
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </div>
                        {showDetails && <div className="kubuni-frame" style={{ paddingBottom: '10px'}}>{details}</div>}
                    </>
                )}

                {/* Non-admin UI */}
                {!isModeAdmin && (
                    <>
                        <div className="media-label" style={{ marginTop: '10px' }}>
                            <strong>
                                <i>{label}</i>
                            </strong>
                            {details && (
                                <button className="btn btn-primary btn-sm expand-button" onClick={toggleDetails} style={{ margin: '0 5px' }} >
                                    {t('DETAILS')} <FontAwesomeIcon icon={showDetails ? faAngleDown : faAngleRight} />
                                </button>
                            )}
                        </div>
                        {showDetails && <div className="kubuni-frame" style={{ paddingBottom: '20px'}}>{details}</div>}
                    </>
                )}
            </div>

            {/* Display short line */}
            {isShortLine && <div className="short-line"></div>}

            {/* Display raw media */}
            {showFullScreen && (
                <div className="full-screen-overlay" onClick={toggleFullScreen}>
                    <img src={getPhotoUrl(media, 'raw')} alt={label} className="full-screen-image" />
                </div>
            )}

            {/* Confirmation modal */}
            <Modal show={showConfirmation} onHide={handleCloseConfirmation}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('CONFIRM_DELETE')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('CONFIRM_DELETE_MESSAGE')}</p>
                </Modal.Body>
                <Modal.Footer  className="justify-content-center">
                    <Button variant="secondary" onClick={handleCloseConfirmation}>
                        {t('CANCEL')}
                    </Button>
                    <Button variant="primary" onClick={handleConfirmDelete}>
                        {t('CONFIRM')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit modal */}
            <Modal show={showEditModal} onHide={handleCloseEditModal} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{t('EDIT')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {availableLanguages.map((language) => (
                            <div key={language}>
                                <Form.Group controlId={`editLabel${ucfirst(language)}`}>
                                    <Form.Label>Titre <Image src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`} className="flag-label" alt={`Titre ${language}`} /></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name={`label${ucfirst(language)}`}
                                        value={editedMedia[`label${ucfirst(language)}`]}
                                        onChange={handleEditInputChange}
                                    />
                                </Form.Group>
                                <Form.Group controlId={`editDetails${ucfirst(language)}`}>
                                    <Form.Label>Description <Image src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`} className="flag-label" alt={`Descriptif ${language}`} /></Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name={`details${ucfirst(language)}`}
                                        value={editedMedia[`details${ucfirst(language)}`]}
                                        onChange={handleEditInputChange}
                                    />
                                </Form.Group>
                            </div>
                        ))}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button variant="secondary" onClick={handleCloseEditModal}>
                        {t('CANCEL')}
                    </Button>
                    <Button variant="primary" onClick={handleEditSubmit}>
                        {t('CONFIRM')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default MediaItem;